import React, { useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import Swal from 'sweetalert2'
import Async from 'react-select/async'
import api from '../services/api'
import { AuthContext } from '../context/AuthContext'
import { MainContext } from '../context/MainContext'
import IClubCalendar, { IClubCalendarAttendance } from '../interfaces/IClubCalendar'

dayjs.locale('pt-br')

const PRESIDENT_ROLES = ['coordinator', 'admin']

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const WEEKDAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

function formatDatePt(dateStr: string) {
  return dayjs(dateStr).format('DD/MM/YYYY')
}

function formatMonthShort(dateStr: string) {
  const d = dayjs(dateStr)
  const weekday = WEEKDAYS_PT[d.day()]
  return weekday + ' ' + d.format('DD') + ' ' + MONTHS_PT[d.month()].substring(0, 3).toUpperCase()
}

const WEEKDAYS_FORM = [
  { label: 'Dom', day: 0 },
  { label: 'Seg', day: 1 },
  { label: 'Ter', day: 2 },
  { label: 'Qua', day: 3 },
  { label: 'Qui', day: 4 },
  { label: 'Sex', day: 5 },
  { label: 'Sáb', day: 6 },
]

const EMPTY_FORM = { title: '', date: '', time: '', description: '', period_start: '', period_end: '' }
const EMPTY_BATCH = { title: '', startDate: '', endDate: '', description: '' }

export default function ClubCalendar() {
  const { authUser } = useContext(AuthContext)
  const { club } = useContext(MainContext)

  const [upcoming, setUpcoming] = useState<IClubCalendar[]>([])
  const [calendarItems, setCalendarItems] = useState<IClubCalendar[]>([])
  const [currentMonth, setCurrentMonth] = useState(dayjs().month())
  const [currentYear, setCurrentYear] = useState(dayjs().year())
  const [selectedItem, setSelectedItem] = useState<IClubCalendar | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<{ title: string; date: string; time: string; description: string; allow_rsvp: boolean; period_start: string; period_end: string }>({ ...EMPTY_FORM, allow_rsvp: false })
  const [loadingCalendar, setLoadingCalendar] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [batchMode, setBatchMode] = useState(false)
  const [batchForm, setBatchForm] = useState(EMPTY_BATCH)
  const [selectedWeekdays, setSelectedWeekdays] = useState<{ day: number; time: string }[]>([])

  // attendance
  const [attendanceSummary, setAttendanceSummary] = useState<any>(null)
  const [attendanceList, setAttendanceList] = useState<any[]>([])
  const [myAttendance, setMyAttendance] = useState<IClubCalendarAttendance | null>(null)
  const [hasCompanions, setHasCompanions] = useState<boolean | null>(null)
  const [companionAdults, setCompanionAdults] = useState(0)
  const [companionChildren, setCompanionChildren] = useState(0)

  const [editingAttendanceId, setEditingAttendanceId] = useState<number | null>(null)
  const [editingAdults, setEditingAdults] = useState(0)
  const [editingChildren, setEditingChildren] = useState(0)
  const [addingParticipant, setAddingParticipant] = useState(false)
  const [newParticipant, setNewParticipant] = useState<any>(null)
  const [newParticipantAdults, setNewParticipantAdults] = useState(0)
  const [newParticipantChildren, setNewParticipantChildren] = useState(0)

  const isPresident = PRESIDENT_ROLES.includes(authUser?.role)
  const clubId = (club as any)?.id

  function isAttendancePeriodOpen(item: IClubCalendar): boolean {
    if (!item.allow_rsvp) return false
    if (!item.period_start && !item.period_end) return true
    const now = new Date()
    if (item.period_start && new Date(item.period_start) > now) return false
    if (item.period_end && new Date(item.period_end) < now) return false
    return true
  }

  useEffect(() => {
    if (clubId) {
      fetchUpcoming()
    }
  }, [clubId])

  useEffect(() => {
    if (clubId && modalOpen) {
      fetchCalendar()
    }
  }, [clubId, currentMonth, currentYear, modalOpen])

  async function fetchUpcoming() {
    try {
      const res = await api.get(`/api/club-calendars/upcoming?club_id=${clubId}`)
      setUpcoming(res.data)
    } catch {
      setUpcoming([])
    }
  }

  async function fetchCalendar() {
    setLoadingCalendar(true)
    try {
      const res = await api.get(
        `/api/club-calendars?club_id=${clubId}&month=${currentMonth + 1}&year=${currentYear}`
      )
      setCalendarItems(res.data)
    } catch {
      setCalendarItems([])
    } finally {
      setLoadingCalendar(false)
    }
  }

  function openCreate() {
    setEditingId(null)
    setForm({ ...EMPTY_FORM, allow_rsvp: false })
    setBatchMode(false)
    setBatchForm(EMPTY_BATCH)
    setSelectedWeekdays([])
    setShowForm(true)
  }

  function toggleWeekday(day: number) {
    setSelectedWeekdays((prev) =>
      prev.find((w) => w.day === day)
        ? prev.filter((w) => w.day !== day)
        : [...prev, { day, time: '' }]
    )
  }

  function setWeekdayTime(day: number, time: string) {
    setSelectedWeekdays((prev) => prev.map((w) => (w.day === day ? { ...w, time } : w)))
  }

  async function handleBatchSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      if (!batchForm.title || !batchForm.startDate || !batchForm.endDate) {
        throw 'Título, data inicial e data final são obrigatórios'
      }
      if (!selectedWeekdays.length) throw 'Selecione ao menos um dia da semana'
      if (selectedWeekdays.some((w) => !w.time)) throw 'Informe o horário de todos os dias selecionados'

      const payload = { ...batchForm, club_id: clubId, weekdays: selectedWeekdays }
      const res = await api.post('/api/club-calendars/batch', payload)

      Swal.fire({
        title: 'Sucesso!',
        text: res.data.message,
        icon: 'success',
        timer: 3000,
        didClose: () => {
          setShowForm(false)
          setBatchMode(false)
          fetchUpcoming()
          fetchCalendar()
        },
      })
    } catch (err: any) {
      Swal.fire({ title: 'Erro!', text: err?.response?.data?.message || err, icon: 'error' })
    }
  }

  function openSelectedItem(item: IClubCalendar) {
    setSelectedItem(item)
    setShowForm(false)
    const att = item.my_attendance ?? null
    setMyAttendance(att)
    setHasCompanions(att ? (att.companion_adults > 0 || att.companion_children > 0 ? true : false) : null)
    setCompanionAdults(att?.companion_adults ?? 0)
    setCompanionChildren(att?.companion_children ?? 0)
    setAttendanceSummary(null)
    setAttendanceList([])
    setAddingParticipant(false)
    setNewParticipant(null)
    setNewParticipantAdults(0)
    setNewParticipantChildren(0)
    if (isPresident && item.allow_rsvp) {
      api.get(`/api/club-calendars/${item.id}/attendances`).then((res) => {
        setAttendanceSummary(res.data.summary)
        setAttendanceList(res.data.attendances)
      }).catch(() => {})
    }
  }

  async function confirmAttendance(item: IClubCalendar) {
    try {
      const adults = hasCompanions ? companionAdults : 0
      const children = hasCompanions ? companionChildren : 0
      await api.post(`/api/club-calendars/${item.id}/attendances`, {
        companion_adults: adults,
        companion_children: children,
      })
      const updated = calendarItems.map((c) =>
        c.id === item.id
          ? { ...c, my_attendance: { id: myAttendance?.id ?? 0, club_calendar_id: item.id, athlete_id: 0, companion_adults: adults, companion_children: children } }
          : c
      )
      setCalendarItems(updated)
      const updatedItem = { ...item, my_attendance: { id: myAttendance?.id ?? 0, club_calendar_id: item.id, athlete_id: 0, companion_adults: adults, companion_children: children } }
      setSelectedItem(updatedItem)
      setMyAttendance(updatedItem.my_attendance)
      setHasCompanions(adults > 0 || children > 0 ? true : false)
      Swal.fire({ title: 'Presença confirmada!', icon: 'success', timer: 2000 })
    } catch (err: any) {
      Swal.fire({ title: 'Erro!', text: err?.response?.data?.message || err, icon: 'error' })
    }
  }

  async function removeAttendance(item: IClubCalendar) {
    try {
      await api['delete'](`/api/club-calendars/${item.id}/attendances`)
      const updated = calendarItems.map((c) =>
        c.id === item.id ? { ...c, my_attendance: null } : c
      )
      setCalendarItems(updated)
      setSelectedItem({ ...item, my_attendance: null })
      setMyAttendance(null)
      setHasCompanions(null)
      setCompanionAdults(0)
      setCompanionChildren(0)
    } catch (err: any) {
      Swal.fire({ title: 'Erro!', text: err?.response?.data?.message || err, icon: 'error' })
    }
  }

  function recalcSummary(list: any[]) {
    const total_members = list.length
    const total_companion_adults = list.reduce((s, a) => s + (a.companion_adults || 0), 0)
    const total_companion_children = list.reduce((s, a) => s + (a.companion_children || 0), 0)
    const total_adults = total_members + total_companion_adults
    const total_children = total_companion_children
    setAttendanceSummary({ total_adults, total_children, total_people: total_adults + total_children })
  }

  async function loadAthleteOptions(inputValue: string) {
    if (!inputValue || inputValue.length < 2) return []
    const res = await api.get(`/api/athletes/getToSelect?search=${inputValue}`)
    return res.data.map((a: any) => ({ label: `${a.member_number || ''} - ${a.full_name}`, value: a.id }))
  }

  async function storeParticipantByPresident(item: IClubCalendar) {
    if (!newParticipant) return
    try {
      const res = await api.post(`/api/club-calendars/${item.id}/attendances/president`, {
        athlete_id: newParticipant.value,
        companion_adults: newParticipantAdults,
        companion_children: newParticipantChildren,
      })
      const updated = [...attendanceList.filter((a) => a.athlete_id !== res.data.athlete_id), res.data]
      setAttendanceList(updated)
      recalcSummary(updated)
      setAddingParticipant(false)
      setNewParticipant(null)
      setNewParticipantAdults(0)
      setNewParticipantChildren(0)
    } catch (err: any) {
      Swal.fire({ title: 'Erro!', text: err?.response?.data?.message || err, icon: 'error' })
    }
  }

  async function updateAttendance(item: IClubCalendar, attendanceId: number) {
    try {
      await api.put(`/api/club-calendars/${item.id}/attendances/${attendanceId}`, {
        companion_adults: editingAdults,
        companion_children: editingChildren,
      })
      const updated = attendanceList.map((a) =>
        a.id === attendanceId ? { ...a, companion_adults: editingAdults, companion_children: editingChildren } : a
      )
      setAttendanceList(updated)
      recalcSummary(updated)
      setEditingAttendanceId(null)
    } catch (err: any) {
      Swal.fire({ title: 'Erro!', text: err?.response?.data?.message || err, icon: 'error' })
    }
  }

  async function removeAttendanceByPresident(item: IClubCalendar, attendanceId: number) {
    Swal.fire({
      title: 'Remover participante?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (!result.isConfirmed) return
      try {
        await api['delete'](`/api/club-calendars/${item.id}/attendances/${attendanceId}`)
        const updated = attendanceList.filter((a) => a.id !== attendanceId)
        setAttendanceList(updated)
        recalcSummary(updated)
      } catch (err: any) {
        Swal.fire({ title: 'Erro!', text: err?.response?.data?.message || err, icon: 'error' })
      }
    })
  }

  function openEdit(item: IClubCalendar) {
    setEditingId(item.id)
    setForm({ title: item.title, date: item.date, time: item.time, description: item.description || '', allow_rsvp: item.allow_rsvp, period_start: item.period_start || '', period_end: item.period_end || '' })
    setSelectedItem(null)
    setShowForm(true)
  }

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      if (!form.title || !form.date || !form.time) {
        throw 'Título, data e horário são obrigatórios'
      }

      const payload = { ...form, club_id: clubId }

      if (editingId) {
        await api.put(`/api/club-calendars/${editingId}`, payload)
      } else {
        await api.post('/api/club-calendars', payload)
      }

      Swal.fire({
        title: 'Sucesso!',
        text: editingId ? 'Compromisso atualizado com sucesso!' : 'Compromisso cadastrado com sucesso!',
        icon: 'success',
        timer: 3000,
        didClose: () => {
          setShowForm(false)
          fetchUpcoming()
          fetchCalendar()
        },
      })
    } catch (err: any) {
      Swal.fire({
        title: 'Erro!',
        text: err?.response?.data?.message || err,
        icon: 'error',
      })
    }
  }

  function handleDelete(item: IClubCalendar) {
    Swal.fire({
      title: 'Tem certeza que deseja excluir este evento?',
      text: 'Esta ação não pode ser desfeita!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        api['delete'](`/api/club-calendars/${item.id}`)
          .then(() => {
            Swal.fire({ title: 'Excluído!', icon: 'success', timer: 2000 })
            setSelectedItem(null)
            fetchUpcoming()
            fetchCalendar()
          })
          .catch((err) => {
            Swal.fire({ title: 'Erro!', text: err?.response?.data?.message, icon: 'error' })
          })
      }
    })
  }

  // Calendar grid
  const firstDayOfMonth = dayjs(new Date(currentYear, currentMonth, 1)).day()
  const daysInMonth = dayjs(new Date(currentYear, currentMonth, 1)).daysInMonth()
  const todayStr = dayjs().format('YYYY-MM-DD')

  function getItemsForDay(day: number): IClubCalendar[] {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return calendarItems.filter((item) => dayjs(item.date).format('YYYY-MM-DD') === dateStr)
  }

  function prevMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear((y) => y - 1)
    } else {
      setCurrentMonth((m) => m - 1)
    }
  }

  function nextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear((y) => y + 1)
    } else {
      setCurrentMonth((m) => m + 1)
    }
  }

  const calendarCells: (number | null)[] = [
    ...Array(firstDayOfMonth).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]

  return (
    <>
      <div
        className="card border-0 rounded-4 text-white position-relative overflow-hidden shadow-sm"
        style={{
          background: 'linear-gradient(135deg, #1a3a5c 0%, #0f2540 60%, #1e4976 100%)',
          minHeight: '234px',
        }}
        data-bs-toggle="modal"
        data-bs-target="#modalClubCalendar"
        role="button"
        tabIndex={0}
        onClick={() => setModalOpen(true)}
      >
        <div
          className="position-absolute"
          style={{
            right: '-10px',
            bottom: '-30px',
            fontSize: '160px',
            lineHeight: '1',
            pointerEvents: 'none',
            transform: 'rotate(-15deg)',
            color: 'rgba(255,255,255,0.05)',
          }}
        >
          <i className="bi bi-calendar3"></i>
        </div>

        <div className="card-body p-4 d-flex flex-column justify-content-between h-100 position-relative z-1">
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <span
                className="badge bg-black bg-opacity-20 px-2 py-1 rounded-pill mb-2 fw-bold"
                style={{ fontSize: '10px', letterSpacing: '0.5px', color: '#a3d4ff' }}
              >
                <i className="bi bi-calendar-event-fill me-1"></i> CALENDÁRIO DO CLUBE
              </span>
              <h5
                className="card-title fw-bold m-0 pb-0"
                style={{ fontSize: '17px', letterSpacing: '0.3px', color: 'rgba(255,255,255,0.75)' }}
              >
                PRÓXIMOS EVENTO
              </h5>
            </div>
            <div
              className="bg-black bg-opacity-20 border border-white border-opacity-10 rounded-3 p-2 d-flex align-items-center justify-content-center"
              style={{ width: '42px', height: '42px', color: '#a3d4ff' }}
            >
              <i className="bi bi-calendar-check-fill fs-4"></i>
            </div>
          </div>

          <div className="pt-2">
            {upcoming.length === 0 ? (
              <p className="mb-2" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
                Nenhuma atividade próxima cadastrada.
              </p>
            ) : (
              <ul className="list-unstyled mb-2">
                {upcoming.map((item) => (
                  <li key={item.id} className="d-flex align-items-center gap-2 mb-1">
                    <span
                      className="fw-bold font-monospace"
                      style={{ fontSize: '11px', color: '#a3d4ff', minWidth: '68px' }}
                    >
                      {formatMonthShort(item.date)}
                    </span>
                    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                      {item.title}
                    </span>
                    <span
                      className="ms-auto font-monospace"
                      style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)' }}
                    >
                      {item.time.substring(0, 5)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="d-flex align-items-center justify-content-between mt-2">
              <span
                className="fw-bold"
                style={{ fontSize: '12px', color: '#a3d4ff', letterSpacing: '0.3px' }}
              >
                <i className="bi bi-calendar3 me-1"></i> Ver calendário
              </span>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '32px', height: '32px', background: 'rgba(255,255,255,0.12)' }}
              >
                <i className="bi bi-arrow-right text-white" style={{ fontSize: '14px' }}></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalClubCalendar"
        tabIndex={-1}
        aria-labelledby="modalClubCalendarLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalClubCalendarLabel">
                📅 Calendário do Clube
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Fechar"
                onClick={() => { setShowForm(false); setSelectedItem(null); setModalOpen(false) }}
              />
            </div>

            <div className="modal-body">
              {isPresident && !showForm && (
                <div className="mb-3">
                  <button className="btn btn-success btn-sm" onClick={openCreate}>
                    <i className="bi bi-plus-circle me-1"></i> Cadastrar evento
                  </button>
                </div>
              )}

              {showForm && (
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="card-title mb-0">
                        {editingId ? 'Editar evento' : batchMode ? 'Cadastro por período' : 'Novo evento'}
                      </h6>
                      {!editingId && (
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className={`btn ${!batchMode ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={() => setBatchMode(false)}
                          >
                            <i className="bi bi-calendar-plus me-1"></i> Único
                          </button>
                          <button
                            type="button"
                            className={`btn ${batchMode ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={() => setBatchMode(true)}
                          >
                            <i className="bi bi-calendar-range me-1"></i> Período
                          </button>
                        </div>
                      )}
                    </div>

                    {!batchMode ? (
                      <form onSubmit={handleFormSubmit} autoComplete="off">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">Título <i className="text-danger">*</i></label>
                            <input type="text" className="form-control" name="title" value={form.title}
                              onChange={handleFormChange} placeholder="Ex: Prova interna" autoFocus />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Data <i className="text-danger">*</i></label>
                            <input type="date" className="form-control" name="date" value={form.date}
                              onChange={handleFormChange} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Horário <i className="text-danger">*</i></label>
                            <input type="time" className="form-control" name="time" value={form.time}
                              onChange={handleFormChange} />
                          </div>
                          <div className="col-12">
                            <label className="form-label">Descrição</label>
                            <textarea className="form-control" name="description" rows={2}
                              value={form.description} onChange={handleFormChange}
                              placeholder="Descrição opcional" />
                          </div>
                          <div className="col-12">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="allow_rsvp"
                                checked={!!form.allow_rsvp}
                                onChange={(e) => setForm((p: any) => ({ ...p, allow_rsvp: e.target.checked, period_start: '', period_end: '' }))}
                              />
                              <label className="form-check-label" htmlFor="allow_rsvp">
                                Permitir confirmação de presença
                              </label>
                            </div>
                          </div>
                          {form.allow_rsvp && (
                            <>
                              <div className="col-12">
                                <label className="form-label small text-muted">Período de confirmação <span className="text-muted">(opcional)</span></label>
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Abertura</label>
                                <input type="datetime-local" className="form-control" value={form.period_start}
                                  onChange={(e) => setForm((p: any) => ({ ...p, period_start: e.target.value }))} />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Encerramento</label>
                                <input type="datetime-local" className="form-control" value={form.period_end}
                                  onChange={(e) => setForm((p: any) => ({ ...p, period_end: e.target.value }))} />
                              </div>
                            </>
                          )}
                          <div className="col-12 d-flex gap-2">
                            <button type="submit" className="btn btn-success">
                              <i className="bi bi-floppy2 me-1"></i>
                              {editingId ? 'Atualizar' : 'Cadastrar'}
                            </button>
                            <button type="button" className="btn btn-danger" onClick={() => setShowForm(false)}>
                              <i className="bi bi-x-circle-fill me-1"></i> Cancelar
                            </button>
                          </div>
                        </div>
                      </form>
                    ) : (
                      <form onSubmit={handleBatchSubmit} autoComplete="off">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">Título <i className="text-danger">*</i></label>
                            <input type="text" className="form-control" value={batchForm.title} autoFocus
                              onChange={(e) => setBatchForm((p) => ({ ...p, title: e.target.value }))}
                              placeholder="Ex: Treino semanal" />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Data inicial <i className="text-danger">*</i></label>
                            <input type="date" className="form-control" value={batchForm.startDate}
                              onChange={(e) => setBatchForm((p) => ({ ...p, startDate: e.target.value }))} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Data final <i className="text-danger">*</i></label>
                            <input type="date" className="form-control" value={batchForm.endDate}
                              onChange={(e) => setBatchForm((p) => ({ ...p, endDate: e.target.value }))} />
                          </div>
                          <div className="col-12">
                            <label className="form-label d-block">Dias da semana <i className="text-danger">*</i></label>
                            <div className="d-flex flex-wrap gap-2">
                              {WEEKDAYS_FORM.map(({ label, day }) => {
                                const selected = selectedWeekdays.find((w) => w.day === day)
                                return (
                                  <div key={day} className="d-flex align-items-center gap-1">
                                    <button
                                      type="button"
                                      className={`btn btn-sm ${selected ? 'btn-success' : 'btn-outline-secondary'}`}
                                      style={{ minWidth: '46px' }}
                                      onClick={() => toggleWeekday(day)}
                                    >
                                      {label}
                                    </button>
                                    {selected && (
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        style={{ width: '100px' }}
                                        value={selected.time}
                                        onChange={(e) => setWeekdayTime(day, e.target.value)}
                                      />
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                          <div className="col-12">
                            <label className="form-label">Descrição</label>
                            <textarea className="form-control" rows={2}
                              value={batchForm.description}
                              onChange={(e) => setBatchForm((p) => ({ ...p, description: e.target.value }))}
                              placeholder="Descrição opcional" />
                          </div>
                          <div className="col-12 d-flex gap-2">
                            <button type="submit" className="btn btn-success">
                              <i className="bi bi-floppy2 me-1"></i> Cadastrar
                            </button>
                            <button type="button" className="btn btn-danger" onClick={() => setShowForm(false)}>
                              <i className="bi bi-x-circle-fill me-1"></i> Cancelar
                            </button>
                          </div>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}

              {selectedItem && !showForm && (
                <div
                  className="rounded-4 mb-4"
                  style={{
                    background: 'linear-gradient(135deg, #1a3a5c 0%, #0f2540 60%, #1e4976 100%)',
                    color: '#fff',
                    boxShadow: '0 4px 24px rgba(15,37,64,0.18)',
                  }}
                >
                  {/* Header do evento */}
                  <div className="d-flex align-items-start justify-content-between px-4 pt-4" style={{ paddingBottom: selectedItem.allow_rsvp ? '8px' : '24px' }}>
                    <div className="flex-grow-1">
                      <span
                        className="badge mb-2 px-2 py-1 rounded-pill fw-bold"
                        style={{ fontSize: '10px', letterSpacing: '0.5px', background: 'rgba(255,255,255,0.12)', color: '#a3d4ff' }}
                      >
                        <i className="bi bi-calendar-event-fill me-1"></i>
                        {formatMonthShort(selectedItem.date)} · {selectedItem.time.substring(0, 5)}
                      </span>
                      <h5 className="fw-bold mb-0" style={{ color: '#fff', letterSpacing: '0.2px' }}>{selectedItem.title}</h5>
                      {selectedItem.description && (
                        <p className="mt-1 mb-0" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', whiteSpace: 'pre-wrap' }}>
                          {selectedItem.description}
                        </p>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 ms-3 flex-shrink-0">
                      {isPresident && (
                        <>
                          <button
                            className="btn btn-sm px-3"
                            style={{ background: 'rgba(255,193,7,0.2)', color: '#ffd966', border: '1px solid rgba(255,193,7,0.3)', borderRadius: '50px', fontSize: '12px' }}
                            onClick={() => openEdit(selectedItem)}
                          >
                            <i className="bi bi-pencil me-1"></i>Editar
                          </button>
                          <button
                            className="btn btn-sm px-3"
                            style={{ background: 'rgba(220,53,69,0.2)', color: '#ff8080', border: '1px solid rgba(220,53,69,0.3)', borderRadius: '50px', fontSize: '12px' }}
                            onClick={() => handleDelete(selectedItem)}
                          >
                            <i className="bi bi-trash me-1"></i>Excluir
                          </button>
                        </>
                      )}
                      <button
                        className="btn btn-sm"
                        style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '50%', width: '32px', height: '32px', padding: 0 }}
                        onClick={() => setSelectedItem(null)}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  </div>

                  {/* Confirmação de presença — visão do sócio */}
                  {!!selectedItem.allow_rsvp && !isPresident && (myAttendance || isAttendancePeriodOpen(selectedItem)) && (
                    <div className="px-4 pb-4 pt-2">
                      <div
                        className="rounded-3 p-3"
                        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                      >
                        {/* Período fechado e já inscrito — readonly */}
                        {!isAttendancePeriodOpen(selectedItem) && myAttendance ? (
                          <div className="d-flex align-items-center gap-3">
                            <i className="bi bi-check-circle-fill" style={{ color: '#20c997', fontSize: '20px' }}></i>
                            <div>
                              <p className="mb-0 fw-bold" style={{ fontSize: '13px', color: '#20c997' }}>Presença confirmada</p>
                              <p className="mb-0" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>O período de confirmação foi encerrado</p>
                            </div>
                            {(myAttendance.companion_adults > 0 || myAttendance.companion_children > 0) && (
                              <div className="d-flex gap-1 ms-auto">
                                {myAttendance.companion_adults > 0 && (
                                  <span className="px-2 py-0 rounded-pill" style={{ fontSize: '11px', background: 'rgba(13,110,253,0.25)', color: '#7eb8ff' }}>
                                    <i className="bi bi-person-fill me-1"></i>{myAttendance.companion_adults}
                                  </span>
                                )}
                                {myAttendance.companion_children > 0 && (
                                  <span className="px-2 py-0 rounded-pill" style={{ fontSize: '11px', background: 'rgba(255,193,7,0.2)', color: '#ffd966' }}>
                                    <i className="bi bi-emoji-smile me-1"></i>{myAttendance.companion_children}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ) : !myAttendance ? (
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <p className="mb-0 fw-bold" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>Vai participar?</p>
                              <p className="mb-0" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Confirme sua presença neste evento</p>
                            </div>
                            <button
                              className="btn fw-bold px-4"
                              style={{ background: 'linear-gradient(90deg,#198754,#20c997)', color: '#fff', border: 'none', borderRadius: '50px', fontSize: '13px', whiteSpace: 'nowrap' }}
                              onClick={() => {
                                setMyAttendance({ id: 0, club_calendar_id: selectedItem.id, athlete_id: 0, companion_adults: 0, companion_children: 0 })
                                setHasCompanions(false)
                                setCompanionAdults(0)
                                setCompanionChildren(0)
                              }}
                            >
                              <i className="bi bi-check-circle-fill me-2"></i>Confirmar
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Acompanhantes */}
                            <p className="mb-2" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>Vai levar acompanhantes?</p>
                            <div className="d-flex gap-2 mb-3">
                              <button
                                type="button"
                                className="btn btn-sm px-3"
                                style={{
                                  borderRadius: '50px',
                                  fontSize: '13px',
                                  background: hasCompanions === false ? 'rgba(220,53,69,0.35)' : 'rgba(255,255,255,0.06)',
                                  color: hasCompanions === false ? '#ff8080' : 'rgba(255,255,255,0.5)',
                                  border: hasCompanions === false ? '1px solid rgba(220,53,69,0.5)' : '1px solid rgba(255,255,255,0.12)',
                                  fontWeight: hasCompanions === false ? 700 : 400,
                                }}
                                onClick={() => { setHasCompanions(false); setCompanionAdults(0); setCompanionChildren(0) }}
                              >
                                Não
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm px-3"
                                style={{
                                  borderRadius: '50px',
                                  fontSize: '13px',
                                  background: hasCompanions === true ? 'rgba(25,135,84,0.35)' : 'rgba(255,255,255,0.06)',
                                  color: hasCompanions === true ? '#20c997' : 'rgba(255,255,255,0.5)',
                                  border: hasCompanions === true ? '1px solid rgba(25,135,84,0.5)' : '1px solid rgba(255,255,255,0.12)',
                                  fontWeight: hasCompanions === true ? 700 : 400,
                                }}
                                onClick={() => setHasCompanions(true)}
                              >
                                Sim
                              </button>
                            </div>

                            {hasCompanions === true && (
                              <div className="d-flex gap-3 mb-3 flex-wrap">
                                {[{ label: 'Adultos', icon: 'bi-person-fill', val: companionAdults, set: setCompanionAdults }, { label: 'Crianças', icon: 'bi-emoji-smile', val: companionChildren, set: setCompanionChildren }].map(({ label, icon, val, set }) => (
                                  <div
                                    key={label}
                                    className="d-flex align-items-center gap-2 rounded-3 px-3 py-2"
                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}
                                  >
                                    <i className={`bi ${icon}`} style={{ color: '#a3d4ff', fontSize: '14px' }}></i>
                                    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', minWidth: '52px' }}>{label}</span>
                                    <button
                                      className="btn btn-sm d-flex align-items-center justify-content-center"
                                      type="button"
                                      style={{ width: '26px', height: '26px', padding: 0, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '50%', fontSize: '16px', lineHeight: 1 }}
                                      onClick={() => set((v: number) => Math.max(0, v - 1))}
                                    >−</button>
                                    <span className="fw-bold" style={{ minWidth: '18px', textAlign: 'center', fontSize: '15px' }}>{val}</span>
                                    <button
                                      className="btn btn-sm d-flex align-items-center justify-content-center"
                                      type="button"
                                      style={{ width: '26px', height: '26px', padding: 0, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '50%', fontSize: '16px', lineHeight: 1 }}
                                      onClick={() => set((v: number) => v + 1)}
                                    >+</button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="d-flex gap-2">
                              {hasCompanions !== null && (
                                <button
                                  className="btn btn-sm fw-bold px-4"
                                  style={{ background: 'linear-gradient(90deg,#198754,#20c997)', color: '#fff', border: 'none', borderRadius: '50px', fontSize: '13px' }}
                                  onClick={() => confirmAttendance(selectedItem)}
                                >
                                  <i className="bi bi-floppy2 me-2"></i>Salvar
                                </button>
                              )}
                              <button
                                className="btn btn-sm fw-bold px-4"
                                style={{ background: 'rgba(220,53,69,0.15)', color: '#ff8080', border: '1px solid rgba(220,53,69,0.25)', borderRadius: '50px', fontSize: '13px' }}
                                onClick={() => removeAttendance(selectedItem)}
                              >
                                <i className="bi bi-calendar-x me-2"></i>Cancelar presença
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Visão do presidente — lista de confirmações */}
                  {!!selectedItem.allow_rsvp && isPresident && (
                    <div className="px-4 pb-4 pt-2">
                      {/* Cards de resumo */}
                      {attendanceSummary && (
                        <div className="d-flex gap-2 mb-3">
                          {[
                            { icon: 'bi-person-fill', label: 'Adultos', val: attendanceSummary.total_adults, color: '#7eb8ff', bg: 'rgba(13,110,253,0.2)' },
                            { icon: 'bi-emoji-smile', label: 'Crianças', val: attendanceSummary.total_children, color: '#ffd966', bg: 'rgba(255,193,7,0.15)' },
                            { icon: 'bi-people-fill', label: 'Total', val: attendanceSummary.total_people, color: '#20c997', bg: 'rgba(25,135,84,0.2)' },
                          ].map(({ icon, label, val, color, bg }) => (
                            <div
                              key={label}
                              className="flex-fill rounded-3 p-2 text-center"
                              style={{ background: bg, border: `1px solid ${color}30` }}
                            >
                              <i className={`bi ${icon}`} style={{ color, fontSize: '16px' }}></i>
                              <p className="mb-0 fw-bold" style={{ fontSize: '18px', color, lineHeight: 1.1 }}>{val}</p>
                              <p className="mb-0" style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.5px' }}>{label.toUpperCase()}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Lista */}
                      <div
                        className="rounded-3"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                      >
                        <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Participantes</span>
                          {!addingParticipant && (
                            <button
                              className="btn btn-sm px-3"
                              style={{ fontSize: '11px', background: 'rgba(25,135,84,0.2)', color: '#20c997', border: '1px solid rgba(25,135,84,0.3)', borderRadius: '50px' }}
                              onClick={() => setAddingParticipant(true)}
                            >
                              <i className="bi bi-plus-circle me-1"></i>Adicionar
                            </button>
                          )}
                        </div>
                        {addingParticipant && (
                          <div className="px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                            <Async
                              loadOptions={loadAthleteOptions}
                              onChange={(opt: any) => setNewParticipant(opt)}
                              value={newParticipant}
                              placeholder="Buscar sócio..."
                              noOptionsMessage={({ inputValue }) => inputValue.length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhum encontrado'}
                              styles={{
                                control: (b) => ({ ...b, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff' }),
                                input: (b) => ({ ...b, color: '#fff' }),
                                singleValue: (b) => ({ ...b, color: '#fff' }),
                                menu: (b) => ({ ...b, background: '#1a3a5c' }),
                                option: (b, s) => ({ ...b, background: s.isFocused ? 'rgba(255,255,255,0.1)' : 'transparent', color: '#fff' }),
                              }}
                            />
                            <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                              {[{ label: 'Adultos', icon: 'bi-person-fill', val: newParticipantAdults, set: setNewParticipantAdults }, { label: 'Crianças', icon: 'bi-emoji-smile', val: newParticipantChildren, set: setNewParticipantChildren }].map(({ label, icon, val, set }) => (
                                <div key={label} className="d-flex align-items-center gap-1 rounded-3 px-2 py-1" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                  <i className={`bi ${icon}`} style={{ color: '#a3d4ff', fontSize: '12px' }}></i>
                                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{label}</span>
                                  <button className="btn btn-sm" type="button" style={{ width: '22px', height: '22px', padding: 0, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '50%', fontSize: '14px', lineHeight: 1 }} onClick={() => set((v: number) => Math.max(0, v - 1))}>−</button>
                                  <span className="fw-bold" style={{ minWidth: '16px', textAlign: 'center', fontSize: '13px' }}>{val}</span>
                                  <button className="btn btn-sm" type="button" style={{ width: '22px', height: '22px', padding: 0, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '50%', fontSize: '14px', lineHeight: 1 }} onClick={() => set((v: number) => v + 1)}>+</button>
                                </div>
                              ))}
                              <button className="btn btn-sm fw-bold px-3" style={{ background: 'linear-gradient(90deg,#198754,#20c997)', color: '#fff', border: 'none', borderRadius: '50px', fontSize: '12px' }} onClick={() => storeParticipantByPresident(selectedItem)}>
                                <i className="bi bi-floppy2 me-1"></i>Salvar
                              </button>
                              <button className="btn btn-sm px-3" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', border: 'none', borderRadius: '50px', fontSize: '12px' }} onClick={() => { setAddingParticipant(false); setNewParticipant(null); setNewParticipantAdults(0); setNewParticipantChildren(0) }}>
                                Cancelar
                              </button>
                            </div>
                          </div>
                        )}
                        {attendanceList.length === 0 ? (
                          <p className="mb-0 py-3 text-center" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>Nenhuma confirmação ainda.</p>
                        ) : (
                          attendanceList.map((a: any, i: number) => (
                            <div
                              key={a.id}
                              className="px-3 py-2"
                              style={{ borderBottom: i < attendanceList.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', flex: 1 }}>
                                  {a.athlete?.full_name}
                                </span>
                                {editingAttendanceId !== a.id && (
                                  <div className="d-flex gap-1">
                                    {a.companion_adults > 0 && (
                                      <span className="px-2 py-0 rounded-pill" style={{ fontSize: '11px', background: 'rgba(13,110,253,0.25)', color: '#7eb8ff' }}>
                                        <i className="bi bi-person-fill me-1"></i>{a.companion_adults}
                                      </span>
                                    )}
                                    {a.companion_children > 0 && (
                                      <span className="px-2 py-0 rounded-pill" style={{ fontSize: '11px', background: 'rgba(255,193,7,0.2)', color: '#ffd966' }}>
                                        <i className="bi bi-emoji-smile me-1"></i>{a.companion_children}
                                      </span>
                                    )}
                                  </div>
                                )}
                                <button
                                  className="btn btn-sm"
                                  style={{ padding: '2px 7px', fontSize: '11px', background: 'rgba(255,193,7,0.15)', color: '#ffd966', border: '1px solid rgba(255,193,7,0.25)', borderRadius: '50px' }}
                                  onClick={() => { setEditingAttendanceId(a.id); setEditingAdults(a.companion_adults); setEditingChildren(a.companion_children) }}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm"
                                  style={{ padding: '2px 7px', fontSize: '11px', background: 'rgba(220,53,69,0.15)', color: '#ff8080', border: '1px solid rgba(220,53,69,0.25)', borderRadius: '50px' }}
                                  onClick={() => removeAttendanceByPresident(selectedItem, a.id)}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                              {editingAttendanceId === a.id && (
                                <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                  {[{ label: 'Adultos', icon: 'bi-person-fill', val: editingAdults, set: setEditingAdults }, { label: 'Crianças', icon: 'bi-emoji-smile', val: editingChildren, set: setEditingChildren }].map(({ label, icon, val, set }) => (
                                    <div key={label} className="d-flex align-items-center gap-1 rounded-3 px-2 py-1" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                      <i className={`bi ${icon}`} style={{ color: '#a3d4ff', fontSize: '12px' }}></i>
                                      <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{label}</span>
                                      <button className="btn btn-sm" type="button" style={{ width: '22px', height: '22px', padding: 0, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '50%', fontSize: '14px', lineHeight: 1 }} onClick={() => set((v: number) => Math.max(0, v - 1))}>−</button>
                                      <span className="fw-bold" style={{ minWidth: '16px', textAlign: 'center', fontSize: '13px' }}>{val}</span>
                                      <button className="btn btn-sm" type="button" style={{ width: '22px', height: '22px', padding: 0, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '50%', fontSize: '14px', lineHeight: 1 }} onClick={() => set((v: number) => v + 1)}>+</button>
                                    </div>
                                  ))}
                                  <button className="btn btn-sm fw-bold px-3" style={{ background: 'linear-gradient(90deg,#198754,#20c997)', color: '#fff', border: 'none', borderRadius: '50px', fontSize: '12px' }} onClick={() => updateAttendance(selectedItem, a.id)}>
                                    <i className="bi bi-floppy2 me-1"></i>Salvar
                                  </button>
                                  <button className="btn btn-sm px-3" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', border: 'none', borderRadius: '50px', fontSize: '12px' }} onClick={() => setEditingAttendanceId(null)}>
                                    Cancelar
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                </div>
              )}

              <div className="d-flex align-items-center justify-content-between mb-3">
                <button className="btn btn-outline-secondary btn-sm" onClick={prevMonth}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <div className="d-flex align-items-center gap-2">
                  <strong>
                    {MONTHS_PT[currentMonth]} {currentYear}
                  </strong>
                  {(currentMonth !== dayjs().month() || currentYear !== dayjs().year()) && (
                    <button
                      className="btn btn-outline-success btn-sm"
                      onClick={() => { setCurrentMonth(dayjs().month()); setCurrentYear(dayjs().year()) }}
                      title="Voltar ao mês atual"
                    >
                      <i className="bi bi-house-fill"></i>
                    </button>
                  )}
                </div>
                <button className="btn btn-outline-secondary btn-sm" onClick={nextMonth}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>

              {loadingCalendar ? (
                <div className="text-center py-4">
                  <span className="spinner-border spinner-border-sm text-success"></span>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered mb-0" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        {WEEKDAYS_PT.map((d, i) => (
                          <th
                            key={d}
                            className="text-center small py-1"
                            style={{
                              width: '14.28%',
                              backgroundColor: i === 0 ? '#fde8e8' : '#e9ecef',
                              color: i === 0 ? '#c0392b' : '#495057',
                            }}
                          >
                            {d}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: Math.ceil(calendarCells.length / 7) }, (_, weekIdx) => (
                        <tr key={weekIdx}>
                          {calendarCells.slice(weekIdx * 7, weekIdx * 7 + 7).map((day, cellIdx) => {
                            const dateStr = day
                              ? `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                              : ''
                            const isToday = dateStr === todayStr
                            const dayItems = day ? getItemsForDay(day) : []

                            return (
                              <td
                                key={cellIdx}
                                className={`p-1 align-top ${isToday ? 'table-success' : ''}`}
                                style={{
                                  minHeight: '80px',
                                  verticalAlign: 'top',
                                  backgroundColor: isToday
                                    ? undefined
                                    : cellIdx === 0
                                    ? 'rgba(220, 53, 69, 0.06)'
                                    : 'rgba(0,0,0,0.02)',
                                }}
                              >
                                {day && (
                                  <>
                                    <div
                                      className={`small fw-bold mb-1 ${isToday ? 'text-success' : 'text-muted'}`}
                                    >
                                      {day}
                                    </div>
                                    {dayItems.map((item) => (
                                      <div
                                        key={item.id}
                                        className="badge bg-success text-wrap text-start w-100 mb-1"
                                        style={{ cursor: 'pointer', fontSize: '11px', lineHeight: '1.3' }}
                                        onClick={() => openSelectedItem(item)}
                                        title={item.title}
                                      >
                                        {item.time.substring(0, 5)} {item.title}
                                      </div>
                                    ))}
                                  </>
                                )}
                              </td>
                            )
                          })}
                          {/* Preenche células vazias no final da última semana */}
                          {calendarCells.slice(weekIdx * 7, weekIdx * 7 + 7).length < 7 &&
                            Array.from({
                              length: 7 - calendarCells.slice(weekIdx * 7, weekIdx * 7 + 7).length,
                            }).map((_, i) => <td key={`empty-end-${i}`} className="p-1" />)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-danger"
                data-bs-dismiss="modal"
                onClick={() => { setShowForm(false); setSelectedItem(null); setModalOpen(false) }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
